import React from 'react';
import { motion } from 'framer-motion';
import { SectionLabel, SectionHeading } from './ui/UI';
/**
 * Evidence — what Periscan delivers and where it's used.
 *
 * Was: 13 use-case chips in a rounded-full tag cloud + 12 pack-contents in
 * a bulleted column + 3 audience cards. Now: two clean editorial lists,
 * one for use cases, one for what's in the pack. No chips, no bullets.
 */
const useCases = ['Customer security reviews', 'SOC 2 readiness', 'ISO 27001 readiness', 'Cyber-insurance renewals', 'Board updates', 'Investor diligence', 'Internal security reviews', 'MSSP client reporting', 'AI app security reviews', 'Remediation proof', 'Security QBRs', 'Vendor diligence', 'M&A assessments'];
const packContents = ['Executive summary', 'Top validated threat paths', 'AI-security validation results', 'Evidence for each path', 'Business impact', 'Remediation plan', 'Verification status', 'Residual risk', 'Control observations', 'Change since last report', 'Technical appendix', 'Shareable summary'];
export function Evidence() {
  return <section id="evidence" className="relative w-full py-32 border-t border-white/5" data-id="element-363">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-364">
        <SectionLabel number="—" label="Evidence" className="mb-8" data-id="element-365" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20" data-id="element-366">
          <div className="lg:col-span-8" data-id="element-367">
            <SectionHeading size="xl" data-id="element-368">
              Security proof
              <br data-id="element-369" />
              <span className="text-white/40" data-id="element-370">you can actually use.</span>
            </SectionHeading>
          </div>
          <div className="lg:col-span-4 lg:pt-4" data-id="element-371">
            <p className="text-lg text-white/60 leading-relaxed" data-id="element-372">
              Periscan helps teams move from screenshots and assertions to
              validated evidence — designed for both technical and non-technical
              audiences.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16" data-id="element-373">
          {/* Use cases — left column */}
          <div className="lg:col-span-5" data-id="element-374">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/40 mb-6" data-id="element-375">
              Use Periscan evidence for
            </p>
            <ul className="border-t border-white/5" data-id="element-376">
              {useCases.map((uc, i) => <motion.li key={uc} initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.3,
              delay: i * 0.02
            }} className="border-b border-white/5 py-3.5 flex items-baseline gap-4" data-id="element-377">
                  <span className="font-mono text-xs text-[#1a8fd4]/60 tracking-[0.2em] w-8 shrink-0" data-id="element-378">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-base text-white/85" data-id="element-379">{uc}</span>
                </motion.li>)}
            </ul>
          </div>

          {/* Pack contents — right column */}
          <div className="lg:col-span-7" data-id="element-380">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/40 mb-6" data-id="element-381">
              What's in an Evidence Pack
            </p>
            <ul className="border-t border-white/5" data-id="element-382">
              {packContents.map((pc, i) => <motion.li key={pc} initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.3,
              delay: i * 0.025
            }} className="border-b border-white/5 py-3.5 flex items-baseline gap-4" data-id="element-383">
                  <span className="font-mono text-xs text-[#1a8fd4]/60 tracking-[0.2em] w-8 shrink-0" data-id="element-384">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-base text-white/85" data-id="element-385">{pc}</span>
                </motion.li>)}
            </ul>

            <p className="mt-10 text-base md:text-lg text-white/65 leading-relaxed max-w-xl" data-id="element-386">
              Proof, context, and next steps. Not an 80-page PDF of doom.
            </p>
          </div>
        </div>
      </div>
    </section>;
}